import {
  CSSProperties,
  FC,
  MouseEvent,
  PointerEvent,
  SyntheticEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { DraggableSyntheticListeners } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import {
  ContextMenu,
  ContextMenuOptionColors,
  ContextMenuOptionType,
  ContextMenuTriggerOnActions
} from '@inno-ui/context-menu'
import {
  ArrowUpRightAndDownLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  MultiplyIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { Spinner } from '@inno-ui/spinner'
import cn from 'clsx'
import {
  WIDGET_COLLAPSED_HEIGHT,
  WIDGET_NATURAL_HEIGHTS,
  WIDGET_NATURAL_WIDTH
} from 'modules/desktop/modules/widgets/constants/widgetDimensions'
import { useDesktopContextMenuState } from 'modules/desktop/pages/MainPage/components/DesktopGrid/DesktopContextMenuContext'
import { useIsWindowMarketplaceOpen } from 'modules/desktop/pages/MainPage/hooks/useIsWindowMarketplaceOpen'
import { isPortalOriginEvent } from 'modules/desktop/pages/MainPage/utils/portalEvents'
import { useTouchContextMenu } from 'modules/desktop/pages/MainPage/utils/useTouchContextMenu'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import styles from './WidgetWrapper.module.scss'
import { WidgetWrapperProps } from './WidgetWrapper.types'

export const WidgetWrapper: FC<WidgetWrapperProps> = ({
  children,
  widget,
  contextMenuOptions,
  isDragging,
  showInUseOverlay,
  hasFullView,
  isInFullView,
  onToggleFullView,
  sortableId,
  isActive,
  showRemoveButton,
  widgetName,
  supportedSizes,
  isCollapsed = false,
  onResize,
  onToggleCollapsed,
  attributes: externalAttributes,
  dragListeners: externalDragListeners,
  transform: externalTransform,
  transition: externalTransition,
  setNodeRef: externalSetNodeRef,
  isLoading = false,
  disableContextMenu = false,
  onRemove,
  onExpand,
  scaleMultiplier = 1,
  fillMode = false,
  isForced = false
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets' })
  const contentId = useId()

  const handleRemoveWidget = () => {
    if (isInFullView) {
      onToggleFullView?.()
    }
    onRemove?.()
  }

  const {
    isContextMenuOpen: isButtonContextMenuOpen,
    setIsContextMenuOpen: setIsButtonContextMenuOpen,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel
  } = useTouchContextMenu({ enabled: !disableContextMenu })
  const {
    isContextMenuOpen: isPointerContextMenuOpen,
    setIsContextMenuOpen: setIsPointerContextMenuOpen
  } = useDesktopContextMenuState()
  const isWidgetMarketplaceOpen = useIsWindowMarketplaceOpen()

  const sortable = useSortable({
    id: sortableId || 'placeholder',
    disabled: !sortableId
  })

  // don't use sortable if in full view mode
  const shouldUseSortable = sortableId && !isInFullView
  const attributes = shouldUseSortable
    ? sortable.attributes
    : externalAttributes
  const dragListeners = shouldUseSortable
    ? sortable.listeners
    : externalDragListeners
  const transform = shouldUseSortable ? sortable.transform : externalTransform
  const transition = shouldUseSortable
    ? sortable.transition
    : externalTransition
  const setNodeRef = shouldUseSortable
    ? sortable.setNodeRef
    : externalSetNodeRef
  // dnd-kit's activators live on the card, so a pointer event a widget raised
  // inside its own portalled modal would start dragging the card underneath it.
  const guardedDragListeners = useMemo(() => {
    if (!dragListeners) return dragListeners
    return Object.fromEntries(
      Object.entries(dragListeners).map(([name, listener]) => [
        name,
        (event: SyntheticEvent) => {
          if (isPortalOriginEvent(event)) return
          listener(event)
        }
      ])
    ) as DraggableSyntheticListeners
  }, [dragListeners])

  const isSortableDragging = shouldUseSortable ? sortable.isDragging : false
  const finalIsDragging = isDragging || isSortableDragging || isActive

  const containerStyle: CSSProperties = isInFullView
    ? {}
    : {
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition
      }

  const width = WIDGET_NATURAL_WIDTH
  const height = WIDGET_NATURAL_HEIGHTS[widget.size]

  // the sidebar box is responsive (240-400px wide), so we scale content down
  // uniformly to fit and center it, rather than relying on the box's own
  // aspect ratio to keep every widget's internal content proportional.
  // we measure widgetBoxRef, not the scaled child, so the transform doesn't
  // affect the measurement.
  const widgetBoxRef = useRef<HTMLDivElement>(null)
  const [gridContentScale, setGridContentScale] = useState(1)
  const [boxWidth, setBoxWidth] = useState<number | null>(null)

  // full view drops the natural aspect ratio, so the content fills the box
  // instead of staying locked to its (much shorter) natural height.
  const fillsBox = fillMode || isInFullView

  useLayoutEffect(() => {
    const node = widgetBoxRef.current
    if (!node) return

    const measure = () => {
      const rect = node.getBoundingClientRect()
      // the box width is independent of the collapse animation, so it stays the
      // source for both the content scale and the expanded height target
      if (rect.width > 0) setBoxWidth(rect.width)
      // fill mode fills the box directly, no scaling needed
      if (fillsBox) return
      // The wrapper's aspect ratio already mirrors the widget's natural
      // dimensions, so width is the stable scaling source. During an in-place
      // size change the browser can briefly report the previous height; using
      // that stale value shrinks the new content until a later layout/refresh.
      const nextScale = Math.min(1, rect.width / width) * scaleMultiplier
      setGridContentScale(nextScale > 0 ? nextScale : 1)
    }
    measure()

    let rafId: number | null = null
    const observer = new ResizeObserver(() => {
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        measure()
      })
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [width, scaleMultiplier, fillsBox])

  const contentStyle: CSSProperties = fillsBox
    ? { width: '100%', height: '100%' }
    : {
        width,
        height,
        // Keep the natural-size content box intact; flex-shrinking it to the
        // preview width before applying the scale would scale it twice and
        // leave empty gutters on either side.
        flexShrink: 0,
        transform: `scale(${gridContentScale})`,
        transformOrigin: 'center'
      }

  const fullViewStyle: CSSProperties = {
    width: '100%',
    maxWidth: WIDGET_NATURAL_WIDTH,
    minWidth: 240
  }

  const regularStyle: CSSProperties = {
    aspectRatio: `${width}/${height}`,
    width: '100%',
    maxWidth: WIDGET_NATURAL_WIDTH,
    minWidth: 240
  }

  // collapsing has to interpolate between two heights, and the expanded one is
  // aspect-ratio derived — so we resolve it to pixels from the measured width
  // and let motion own `height` instead of CSS.
  const expandedHeight = boxWidth != null ? (boxWidth * height) / width : null
  const heightTarget = isInFullView
    ? '100%'
    : isCollapsed
      ? WIDGET_COLLAPSED_HEIGHT
      : expandedHeight

  // motion owns `height`, so the box must not carry an inline one — it would
  // snap before the animation runs. aspect-ratio/min-height only hold the right
  // size until motion takes over on mount.
  const widgetStyle: CSSProperties = isInFullView
    ? fullViewStyle
    : {
        width: '100%',
        maxWidth: WIDGET_NATURAL_WIDTH,
        minWidth: 240,
        ...(isCollapsed
          ? { minHeight: WIDGET_COLLAPSED_HEIGHT }
          : { aspectRatio: `${width}/${height}` })
      }

  const prefersReducedMotion = useReducedMotion()
  const collapseTransition = {
    duration: prefersReducedMotion ? 0 : 0.24,
    ease: 'easeOut' as const
  }
  // the transition has to be right on the render the target changes, so the
  // toggle is detected during render rather than from an effect
  const previousIsCollapsed = useRef(isCollapsed)
  const isCollapseToggle = previousIsCollapsed.current !== isCollapsed
  const previousSize = useRef(widget.size)
  const isSizeToggle = previousSize.current !== widget.size
  const [isCollapseSettled, setIsCollapseSettled] = useState(isCollapsed)
  const isContentHidden = isCollapsed && isCollapseSettled

  useLayoutEffect(() => {
    previousIsCollapsed.current = isCollapsed
    previousSize.current = widget.size
  }, [isCollapsed, widget.size])

  const handleHeightAnimationComplete = () => {
    setIsCollapseSettled(isCollapsed)
  }

  // Menu surfaces are portalled too, so match the card's own by popover group.
  const contextMenuGroupId = `widget-context-menu-${contentId}`

  const isEventFromOwnContextMenu = (target: EventTarget) => {
    if (!(target instanceof Element)) return false
    const surface = target.closest('[data-popover="popover-no-close"]')
    return surface?.getAttribute('data-popover-group') === contextMenuGroupId
  }

  // @inno-ui/popover's trigger opens on any contextmenu, wherever it came from.
  const handleContextMenuCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!isPortalOriginEvent(event)) return
    if (isEventFromOwnContextMenu(event.target)) return
    event.stopPropagation()
  }

  const handleClick = (e: MouseEvent) => {
    if (isPortalOriginEvent(e)) return
    if (isCollapsed) return
    // in full view the content owns the whole surface, so a bubbled click from
    // one of its own controls must not be read as "close the full view" — the
    // minimize button and the backdrop are the ways out.
    if (isInFullView) return
    if (onExpand) {
      e.stopPropagation()
      onExpand()
      return
    }
    if (!hasFullView) return
    e.stopPropagation()
    onToggleFullView?.()
  }

  const stopPointerPropagation = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }

  const handleToggleCollapsed = () => {
    setIsButtonContextMenuOpen(false)
    setIsPointerContextMenuOpen(false)
    onToggleCollapsed?.()
  }

  const sizeOptions: ContextMenuOptionType[] =
    supportedSizes && supportedSizes.length > 1 && onResize
      ? [
          {
            icon: ArrowUpRightAndDownLeftIcon,
            label: t('size'),
            options: supportedSizes.map(size => ({
              label: t(`sizes.${size}`),
              isSelected: widget.size === size,
              disabled: widget.size === size,
              onClick: () => onResize(size)
            }))
          }
        ]
      : []

  // resizing from full view would apply to the slot the widget returns to,
  // which the user can't see — so the right-click menu drops these too, matching
  // the hidden hover actions.
  const presentationOptions: ContextMenuOptionType[] = isInFullView
    ? []
    : sizeOptions

  const menuOptions: ContextMenuOptionType[] = [
    ...(contextMenuOptions || []),
    ...(contextMenuOptions?.length && presentationOptions.length
      ? [{ isSeparator: true }]
      : []),
    ...presentationOptions,
    ...(!isForced && (contextMenuOptions?.length || presentationOptions.length)
      ? [{ isSeparator: true }]
      : []),
    ...(isForced
      ? []
      : [
          {
            icon: TrashLineIcon,
            label: t('remove_widget'),
            color: ContextMenuOptionColors.Red,
            onClick: handleRemoveWidget
          }
        ])
  ]

  const hasContextMenuOptions = menuOptions.some(option => !option.isSeparator)
  const shouldShowContextMenu =
    !disableContextMenu && 'id' in widget && hasContextMenuOptions
  const shouldShowExpandedActions =
    !isInFullView && (Boolean(onToggleCollapsed) || shouldShowContextMenu)
  // full view is a focused surface — collapsing and resizing don't apply there,
  // so its only chrome is the button that takes the widget back to the grid
  const shouldShowFullViewActions = isInFullView && Boolean(onToggleFullView)
  const shouldKeepActionsVisible =
    isButtonContextMenuOpen || isPointerContextMenuOpen

  const renderContextMenuButton = () => (
    <ContextMenu
      popoverGroupId={contextMenuGroupId}
      isOpen={isButtonContextMenuOpen}
      setIsOpen={setIsButtonContextMenuOpen}
      triggerNode={
        <Button
          label={t('widget_actions', { name: widgetName })}
          icon={EllipsisVerticalIcon}
          isHiddenLabel
          size={ButtonSizes.S}
          variant={isCollapsed ? ButtonVariants.Ghost : ButtonVariants.Dark}
          color={ButtonColors.Blue}
          aria-label={t('widget_actions', { name: widgetName })}
          aria-haspopup='menu'
          aria-expanded={isButtonContextMenuOpen}
        />
      }
      triggerOnAction={ContextMenuTriggerOnActions.OnClick}
      triggerWrapperClassName={styles.contextMenuTrigger}
      options={menuOptions}
      zIndex={10}
      stopEventPropagation
    />
  )

  const renderTriggerContent = () => (
    <div
      className={cn(styles.noContextMenuWrapper, {
        [styles.centered]: !fillsBox
      })}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchCancel={handleTouchCancel}
    >
      {!isCollapsed &&
        (shouldShowExpandedActions || shouldShowFullViewActions) && (
          <div
            className={cn(styles.actionsButtonWrapper, {
              // the way out of full view can't be hover-gated
              [styles.actionsVisible]: shouldKeepActionsVisible || isInFullView
            })}
            onPointerDown={stopPointerPropagation}
            onClick={event => event.stopPropagation()}
          >
            {shouldShowFullViewActions ? (
              <Button
                label={t('exit_full_view', { name: widgetName })}
                icon={ArrowUpRightAndDownLeftIcon}
                isHiddenLabel
                size={ButtonSizes.S}
                variant={ButtonVariants.Dark}
                color={ButtonColors.Blue}
                aria-label={t('exit_full_view', { name: widgetName })}
                onClick={() => onToggleFullView?.()}
              />
            ) : (
              <>
                {onToggleCollapsed && (
                  <Button
                    label={t('collapse_widget', { name: widgetName })}
                    icon={ChevronUpIcon}
                    isHiddenLabel
                    size={ButtonSizes.S}
                    variant={ButtonVariants.Dark}
                    color={ButtonColors.Blue}
                    aria-label={t('collapse_widget', { name: widgetName })}
                    aria-expanded={true}
                    aria-controls={contentId}
                    onClick={handleToggleCollapsed}
                  />
                )}
                {shouldShowContextMenu && renderContextMenuButton()}
              </>
            )}
          </div>
        )}
      {showInUseOverlay && (
        <div className={styles.inUseOverlay}>
          <div className={styles.label}>IN USE</div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {isCollapsed && (
          <motion.div
            className={styles.collapsedContent}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={collapseTransition}
          >
            <div className={styles.collapsedIdentity}>
              <span className={styles.collapsedName}>
                {widgetName ?? t('widget')}
              </span>
              <span className={styles.collapsedSize}>
                {t(`sizes.${widget.size}`)}
              </span>
            </div>
            <div
              className={cn(styles.collapsedActions, {
                [styles.actionsVisible]: shouldKeepActionsVisible
              })}
              onPointerDown={stopPointerPropagation}
              onClick={event => event.stopPropagation()}
            >
              <Button
                label={t('expand_widget', { name: widgetName })}
                icon={ChevronDownIcon}
                isHiddenLabel
                size={ButtonSizes.S}
                variant={ButtonVariants.Ghost}
                color={ButtonColors.Blue}
                aria-label={t('expand_widget', { name: widgetName })}
                aria-expanded={false}
                aria-controls={contentId}
                onClick={handleToggleCollapsed}
              />
              {shouldShowContextMenu && renderContextMenuButton()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        id={contentId}
        style={contentStyle}
        hidden={isContentHidden}
        initial={false}
        animate={{ opacity: isCollapsed ? 0 : 1 }}
        transition={collapseTransition}
      >
        {children}
      </motion.div>
    </div>
  )

  useEffect(() => {
    if (isWidgetMarketplaceOpen && isInFullView) {
      onToggleFullView?.()
    }
  }, [isWidgetMarketplaceOpen, isInFullView, onToggleFullView])

  return (
    <>
      {isInFullView && (
        <div
          style={regularStyle}
          className={cn(styles.sortableContainer, styles.fullViewPlaceholder)}
        >
          <p className={styles.description}>{t('full_view_placeholder')}</p>
        </div>
      )}
      <div
        ref={setNodeRef}
        style={containerStyle}
        className={cn(styles.sortableContainer, {
          [styles.isDragging]: finalIsDragging || isActive,
          [styles.fullView]: isInFullView
        })}
      >
        {showRemoveButton && !isForced && !isInFullView && 'id' in widget && (
          <div className={styles.removeButtonWrapper}>
            <Button
              variant={ButtonVariants.Dark}
              size={ButtonSizes.S}
              icon={MultiplyIcon}
              color={ButtonColors.Red}
              label={t('remove_widget')}
              isHiddenLabel
              aria-label={t('remove_widget')}
              onClick={handleRemoveWidget}
            />
          </div>
        )}
        <motion.div
          ref={widgetBoxRef}
          className={cn(styles.widgetWrapper, {
            [styles.dragging]: finalIsDragging,
            [styles.fullView]: isInFullView,
            [styles.collapsed]: isCollapsed,
            [styles.draggable]: Boolean(sortableId) && !isForced,
            [styles.hasFullView]:
              !isInFullView && (hasFullView || Boolean(onExpand))
          })}
          style={widgetStyle}
          initial={false}
          animate={heightTarget != null ? { height: heightTarget } : undefined}
          // an explicit size change or collapse toggle should ease; purely
          // width-driven height updates (sidebar resize) must land instantly
          transition={
            isCollapseToggle || isSizeToggle
              ? collapseTransition
              : { duration: 0 }
          }
          onAnimationComplete={handleHeightAnimationComplete}
          onClick={handleClick}
          onContextMenuCapture={handleContextMenuCapture}
          {...(isInFullView ? {} : attributes)}
          {...(isInFullView ? {} : guardedDragListeners)}
        >
          {isLoading && (
            <div className={styles.loaderWrapper}>
              <Spinner />
            </div>
          )}
          {shouldShowContextMenu ? (
            <ContextMenu
              popoverGroupId={contextMenuGroupId}
              isOpen={isPointerContextMenuOpen}
              setIsOpen={setIsPointerContextMenuOpen}
              triggerNode={renderTriggerContent()}
              triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
              triggerWrapperClassName={styles.widgetContextMenuTrigger}
              showAtClickPoint
              options={menuOptions}
              zIndex={10}
              stopEventPropagation
            />
          ) : (
            renderTriggerContent()
          )}
        </motion.div>
      </div>
    </>
  )
}
